import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { MatchJobModal } from './MatchJobModal';

interface UnmatchedEvent {
  date: string;
  timeRange: string;
  worker: string;
  eventName: string;
  hours: number;
  eventId?: string;
  calendarId?: string;
}

interface UnmatchedEventsSectionProps {
  unmatchedEvents: UnmatchedEvent[];
  onEventMatched: () => void;
}

export function UnmatchedEventsSection({ unmatchedEvents, onEventMatched }: UnmatchedEventsSectionProps) {
  const [selectedEvent, setSelectedEvent] = useState<UnmatchedEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleMatchClick = (event: UnmatchedEvent) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleMatchSuccess = () => {
    setSelectedEvent(null);
    setIsModalOpen(false);
    onEventMatched();
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4">Unmatched Events</h2>
      {unmatchedEvents.length === 0 ? (
        <p className="text-green-600">All events were successfully matched to jobs</p>
      ) : (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            These events could not be matched to any job in the system
          </p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Time Range</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Worker</TableHead>
                  <TableHead>Event Name</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmatchedEvents.map((event, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{event.date}</TableCell>
                    <TableCell>{event.timeRange}</TableCell>
                    <TableCell>{event.hours.toFixed(2)}</TableCell>
                    <TableCell>{event.worker}</TableCell>
                    <TableCell>{event.eventName}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMatchClick(event)}
                        disabled={!event.eventId || !event.calendarId}
                      >
                        Match to Job
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {selectedEvent && selectedEvent.eventId && selectedEvent.calendarId && (
        <MatchJobModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          eventId={selectedEvent.eventId}
          calendarId={selectedEvent.calendarId}
          eventName={selectedEvent.eventName}
          onMatchSuccess={handleMatchSuccess}
        />
      )}
    </div>
  );
}
